/**
 * Reads the payload out of a JWT.
 *
 * The reason this exists rather than `atob(token.split(".")[1])`: JWT segments
 * are **base64url**, not base64. They use `-` and `_` where base64 uses `+` and
 * `/`, and they drop the `=` padding. `atob` only understands standard base64,
 * so a token whose payload happens to encode to either of those characters —
 * which depends on the byte values, so it is intermittent rather than never —
 * throws `InvalidCharacterError` or decodes to nonsense.
 *
 * That made it a decode which works in testing and fails for some real users,
 * which is the worst kind. It was duplicated at two call sites before this.
 */
export interface JwtPayload {
    /** Expiry, epoch seconds. */
    exp?: number;
    [claim: string]: unknown;
}

export class JwtDecodeError extends Error {
    constructor(message: string) {
        super(message);
        this.name = "JwtDecodeError";
    }
}

export function decodeJwtPayload(token: string): JwtPayload {
    const segment = token?.split(".")[1];
    if (!segment) {
        throw new JwtDecodeError("Token has no payload segment.");
    }

    try {
        return JSON.parse(base64UrlDecode(segment));
    } catch (e) {
        throw new JwtDecodeError(`Could not read the token payload: ${(e as Error).message}`);
    }
}

function base64UrlDecode(segment: string): string {
    const base64 = segment.replace(/-/g, "+").replace(/_/g, "/");
    // atob rejects an unpadded string; base64url omits the padding.
    const padded = base64.padEnd(base64.length + ((4 - (base64.length % 4)) % 4), "=");

    // decodeURIComponent/escape round-trip so multi-byte UTF-8 in a claim (an
    // accented name, say) survives. atob alone yields latin-1 bytes.
    return decodeURIComponent(
        atob(padded)
            .split("")
            .map((c) => `%${`00${c.charCodeAt(0).toString(16)}`.slice(-2)}`)
            .join("")
    );
}
